import React from 'react';
import { Link } from 'react-router-dom';
import { Users, UserX, Repeat, ExternalLink, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { roleLabel, fmtDate } from './attributionApi';

function PartyName({ user }) {
  if (user?.restricted) return <span className="inline-flex items-center gap-1 text-gray-500"><Lock className="w-3 h-3" /> Restricted</span>;
  const name = user?.affiliate_id
    ? <Link to={`/affiliates/${user.affiliate_id}`} className="hover:underline" data-testid={`partner-link-${user.affiliate_id}`}>{user.name}</Link>
    : (user?.name || 'Unknown');
  return (
    <span className="inline-flex items-center gap-1">
      {name}
      {user?.external && <ExternalLink className="w-3 h-3 text-gray-400" title="Outside party" />}
    </span>
  );
}

function RosterRow({ r, canEdit, onChange, onEnd }) {
  const ended = r.status !== 'active';
  return (
    <div data-testid={`relationship-row-${r.id}`}
         className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${ended ? 'border-dashed border-gray-200 dark:border-gray-700 opacity-60' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300'}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Users className={`w-4 h-4 shrink-0 ${ended ? 'text-gray-400' : 'text-blue-500'}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white"><PartyName user={r.user} /></p>
          <p className="text-xs text-gray-500">
            {fmtDate(r.effective_from)} → {ended ? fmtDate(r.effective_to) : 'current'}
            {r.ended_reason && <> · {r.ended_reason}</>}
            {r.scope && r.scope !== 'merchant_group' && <> · {roleLabel(r.scope)}</>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full capitalize" data-testid={`relationship-role-${r.id}`}>
          {roleLabel(r.role)}
        </span>
        {!ended && canEdit(r) && (
          <>
            <Button size="sm" variant="ghost" onClick={() => onChange(r)} data-testid={`relationship-change-${r.id}`}><Repeat className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={() => onEnd(r)} data-testid={`relationship-end-${r.id}`}><UserX className="w-3.5 h-3.5" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

export function RelationshipRoster({ relationships, canEdit, onChange, onEnd }) {
  if (!relationships.length) {
    return <p className="text-sm text-gray-500 py-6 text-center" data-testid="relationships-empty">No relationships assigned yet</p>;
  }
  return (
    <div className="space-y-2" data-testid="relationship-roster">
      {relationships.map((r) => <RosterRow key={r.id} r={r} canEdit={canEdit} onChange={onChange} onEnd={onEnd} />)}
    </div>
  );
}
